import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';
import { Plus, Trash2, GraduationCap, Search, Sparkles, Loader2, BookOpen, MapPin } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const TYPE_COLORS = {
  reach: 'bg-red-50 text-red-700 border-red-200',
  target: 'bg-blue-50 text-blue-700 border-blue-200',
  safety: 'bg-green-50 text-green-700 border-green-200',
};

export default function CollegeList() {
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [matchScores, setMatchScores] = useState({});
  const [scoringId, setScoringId] = useState(null);
  const [form, setForm] = useState({
    name: '', college_type: 'target', acceptance_rate: '', early_decision: false,
    deadline: '', supplemental_essays_required: 0, notes: '',
  });

  // Research state
  const [researching, setResearching] = useState(false);
  const [researchData, setResearchData] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  const load = () => {
    api.getColleges().then(setColleges).catch(() => toast.error('Failed to load colleges')).finally(() => setLoading(false));
    api.getAllMatchScores().then((scores) => {
      const map = {};
      scores.forEach((s) => { map[s.college_id] = s; });
      setMatchScores(map);
    }).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  // Debounced suggestions
  const handleNameChange = useCallback((value) => {
    setForm((prev) => ({ ...prev, name: value }));
    setResearchData(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length >= 3) {
      debounceRef.current = setTimeout(async () => {
        try {
          const results = await api.suggestSchools(value);
          setSuggestions(results);
          setShowSuggestions(results.length > 0);
        } catch {
          setSuggestions([]);
        }
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, []);

  const handleResearch = async () => {
    if (!form.name || form.name.length < 3) {
      toast.error('Enter a college name first');
      return;
    }
    setResearching(true);
    setResearchData(null);
    try {
      const data = await api.researchSchool(form.name);
      setResearchData(data);
      // Auto-populate form fields
      setForm((prev) => ({
        ...prev,
        name: data.name || prev.name,
        acceptance_rate: data.acceptance_rate != null ? String(data.acceptance_rate) : prev.acceptance_rate,
        college_type: data.college_type_suggestion || prev.college_type,
        early_decision: data.has_early_decision || false,
        deadline: data.regular_deadline || prev.deadline,
        supplemental_essays_required: data.supplemental_essays_required || prev.supplemental_essays_required,
      }));
      toast.success(`Research loaded for ${data.name}`);
    } catch (err) {
      toast.error(err.message || 'Could not research this school');
    } finally {
      setResearching(false);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setForm((prev) => ({ ...prev, name: suggestion.name }));
    setShowSuggestions(false);
    // Trigger research for this suggestion
    setTimeout(() => {
      setForm((prev) => ({ ...prev, name: suggestion.name }));
      handleResearchByName(suggestion.name);
    }, 50);
  };

  const handleResearchByName = async (name) => {
    setResearching(true);
    setResearchData(null);
    try {
      const data = await api.researchSchool(name);
      setResearchData(data);
      setForm((prev) => ({
        ...prev,
        name: data.name || prev.name,
        acceptance_rate: data.acceptance_rate != null ? String(data.acceptance_rate) : prev.acceptance_rate,
        college_type: data.college_type_suggestion || prev.college_type,
        early_decision: data.has_early_decision || false,
        deadline: data.regular_deadline || prev.deadline,
        supplemental_essays_required: data.supplemental_essays_required || prev.supplemental_essays_required,
      }));
      toast.success(`Research loaded for ${data.name}`);
    } catch (err) {
      toast.error(err.message || 'Could not research this school');
    } finally {
      setResearching(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        acceptance_rate: form.acceptance_rate ? parseFloat(form.acceptance_rate) : null,
        deadline: form.deadline || null,
        supplemental_essays_required: parseInt(form.supplemental_essays_required) || 0,
      };
      await api.createCollege(payload);
      toast.success(`${form.name} added!`);
      setForm({ name: '', college_type: 'target', acceptance_rate: '', early_decision: false, deadline: '', supplemental_essays_required: 0, notes: '' });
      setResearchData(null);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove ${name} from your list?`)) return;
    try {
      await api.deleteCollege(id);
      toast.success(`${name} removed`);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleStatusChange = async (id, status) => {
    try { await api.updateCollege(id, { status }); load(); } catch (err) { toast.error(err.message); }
  };

  const handleMatchScore = async (collegeId) => {
    setScoringId(collegeId);
    try {
      const result = await api.getMatchScore(collegeId);
      setMatchScores((prev) => ({ ...prev, [collegeId]: result }));
      toast.success('Match score generated!');
    } catch (err) {
      toast.error(err.message || 'Failed to generate match score');
    } finally {
      setScoringId(null);
    }
  };

  const filtered = colleges
    .filter((c) => filter === 'all' || c.college_type === filter)
    .filter((c) => c.name.toLowerCase().includes(search.toLowerCase()));

  if (loading) {
    return <div className="space-y-4 animate-pulse">{[1,2,3].map(i => <div key={i} className="h-20 bg-[#E2E2E2] rounded-xl" />)}</div>;
  }

  return (
    <div className="space-y-6" data-testid="college-list-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-medium tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>College List</h1>
          <p className="text-sm text-muted-foreground mt-1">{colleges.length} schools on your list</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setResearchData(null); setSuggestions([]); setShowSuggestions(false); } }}>
          <DialogTrigger asChild>
            <Button className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white" data-testid="add-college-btn">
              <Plus className="h-4 w-4 mr-2" /> Add College
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>Add College</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              {/* Name with Research */}
              <div className="space-y-2">
                <Label>College Name</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      ref={inputRef}
                      value={form.name}
                      onChange={(e) => handleNameChange(e.target.value)}
                      onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                      placeholder="e.g., Harvard University"
                      required
                      data-testid="college-name-input"
                    />
                    {/* Suggestions dropdown */}
                    {showSuggestions && (
                      <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-[#E2E2E2] rounded-lg shadow-lg overflow-hidden" data-testid="school-suggestions">
                        {suggestions.map((s, i) => (
                          <button
                            key={i}
                            type="button"
                            className="w-full text-left px-3 py-2 hover:bg-[#F2F2F0] transition-colors flex items-center justify-between"
                            onMouseDown={() => handleSuggestionClick(s)}
                            data-testid={`suggestion-${i}`}
                          >
                            <span className="text-sm">{s.name}</span>
                            {s.location && <span className="text-xs text-muted-foreground">{s.location}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleResearch}
                    disabled={researching || form.name.length < 3}
                    className="flex-shrink-0"
                    data-testid="research-btn"
                  >
                    {researching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    <span className="ml-1.5 hidden sm:inline">Research</span>
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">Type a school name and click Research to auto-populate data</p>
              </div>

              {/* Research Results Banner */}
              {researchData && (
                <div className="bg-[#0F392B]/5 rounded-lg p-4 border border-[#0F392B]/10 space-y-3" data-testid="research-results">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-[#D4AF37]" />
                    <span className="text-sm font-medium">Auto-populated from research</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {researchData.location && (
                      <div className="flex items-center gap-1.5">
                        <MapPin className="h-3 w-3 text-muted-foreground" />
                        <span>{researchData.location}</span>
                      </div>
                    )}
                    {researchData.type && <span className="capitalize">{researchData.type} university</span>}
                    {researchData.sat_range && <span>SAT: {researchData.sat_range}</span>}
                    {researchData.act_range && <span>ACT: {researchData.act_range}</span>}
                    {researchData.average_gpa && <span>Avg GPA: {researchData.average_gpa}</span>}
                    {researchData.application_fee && <span>Fee: ${researchData.application_fee}</span>}
                  </div>
                  {researchData.notable_programs && researchData.notable_programs.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {researchData.notable_programs.slice(0, 5).map((p, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{p}</Badge>
                      ))}
                    </div>
                  )}
                  {researchData.supplemental_essays && researchData.supplemental_essays.length > 0 && (
                    <div>
                      <div className="flex items-center gap-1.5 mb-1">
                        <BookOpen className="h-3 w-3 text-muted-foreground" />
                        <span className="text-xs font-medium">Supplemental Essays ({researchData.supplemental_essays.length})</span>
                      </div>
                      <div className="space-y-1.5">
                        {researchData.supplemental_essays.slice(0, 3).map((e, i) => (
                          <p key={i} className="text-xs text-muted-foreground bg-white rounded px-2 py-1.5 border border-[#E2E2E2]">
                            {e.prompt?.substring(0, 120)}{e.prompt?.length > 120 ? '...' : ''} {e.word_limit && <span className="text-[#D4AF37]">({e.word_limit} words)</span>}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={form.college_type} onValueChange={(v) => setForm({ ...form, college_type: v })}>
                    <SelectTrigger data-testid="college-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="reach">Reach</SelectItem>
                      <SelectItem value="target">Target</SelectItem>
                      <SelectItem value="safety">Safety</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Acceptance Rate (%)</Label>
                  <Input type="number" min="0" max="100" step="0.1" value={form.acceptance_rate} onChange={(e) => setForm({ ...form, acceptance_rate: e.target.value })} placeholder="e.g., 5.2" data-testid="college-rate-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Deadline</Label>
                <Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} data-testid="college-deadline-input" />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes..." data-testid="college-notes-input" />
              </div>
              <Button type="submit" className="w-full bg-[#0F392B] hover:bg-[#0F392B]/90 text-white" data-testid="college-submit-btn">Add to List</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search colleges..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="college-search" />
        </div>
        <div className="flex gap-2">
          {['all', 'reach', 'target', 'safety'].map((f) => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              className={filter === f ? 'bg-[#0F392B] text-white' : ''}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {/* College Cards */}
      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <GraduationCap className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-muted-foreground">No colleges yet</p>
          <p className="text-sm text-muted-foreground mt-1">Click &ldquo;Add College&rdquo; to start building your list</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => (
            <Card key={c.id} className="border-[#E2E2E2] shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-md transition-shadow" data-testid={`college-card-${c.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-medium text-base leading-tight">{c.name}</h3>
                  <button onClick={() => handleDelete(c.id, c.name)} className="text-muted-foreground hover:text-red-500 transition-colors p-1" data-testid={`delete-college-${c.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex gap-2 mb-3">
                  <Badge variant="outline" className={`text-xs capitalize ${TYPE_COLORS[c.college_type] || ''}`}>{c.college_type}</Badge>
                  {c.acceptance_rate && <Badge variant="outline" className="text-xs">{c.acceptance_rate}%</Badge>}
                </div>
                {c.deadline && (
                  <p className="text-xs text-muted-foreground mb-3">
                    Deadline: {new Date(c.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                )}
                {c.common_app && (
                  <div className="mt-1 mb-3 rounded-lg bg-[#F9F9F7] border border-[#E2E2E2] p-3" data-testid={`common-app-strip-${c.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[9px] font-bold uppercase tracking-widest text-[#D4AF37]">Common App</p>
                      <a href={`/deadlines-grid/${c.common_app.slug}`} target="_blank" rel="noreferrer" className="text-[10px] text-[#0F392B] hover:underline">See grid entry ↗</a>
                    </div>
                    <div className="grid grid-cols-3 gap-1.5 mb-2">
                      {c.common_app.ed && (<div><p className="text-[8px] uppercase text-muted-foreground">ED</p><p className="text-[10px] font-semibold text-[#0F392B]">{c.common_app.ed}</p></div>)}
                      {c.common_app.rea && (<div><p className="text-[8px] uppercase text-muted-foreground">REA</p><p className="text-[10px] font-semibold text-[#0F392B]">{c.common_app.rea}</p></div>)}
                      {c.common_app.ea && (<div><p className="text-[8px] uppercase text-muted-foreground">EA</p><p className="text-[10px] font-semibold text-[#0F392B]">{c.common_app.ea}</p></div>)}
                      {c.common_app.rd_rolling && (<div><p className="text-[8px] uppercase text-muted-foreground">RD</p><p className="text-[10px] font-semibold text-[#0F392B]">{c.common_app.rd_rolling}</p></div>)}
                    </div>
                    <div className="flex flex-wrap gap-1.5 pt-2 border-t border-[#E2E2E2]">
                      {c.common_app.fee_us && <span className="inline-block px-1.5 py-0.5 rounded bg-white border border-[#E2E2E2] text-[9px] font-medium text-[#0F392B]">Fee {c.common_app.fee_us}{c.common_app.fee_intl && c.common_app.fee_intl !== c.common_app.fee_us ? ` / ${c.common_app.fee_intl}` : ''}</span>}
                      {c.common_app.test_policy && <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold ${
                        c.common_app.test_policy === 'A' ? 'bg-[#0F392B] text-white' :
                        c.common_app.test_policy === 'N' || c.common_app.test_policy === 'I' ? 'bg-white text-[#0F392B] border border-[#E2E2E2]' :
                        'bg-[#D4AF37]/20 text-[#0F392B]'
                      }`}>Tests: {c.common_app.test_policy}</span>}
                      {c.common_app.te && <span className="inline-block px-1.5 py-0.5 rounded bg-white border border-[#E2E2E2] text-[9px] font-medium text-[#0F392B]">TE: {c.common_app.te}</span>}
                    </div>
                  </div>
                )}
                {c.notes && <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{c.notes}</p>}
                <Select value={c.status || 'not_started'} onValueChange={(v) => handleStatusChange(c.id, v)}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`status-select-${c.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_started">Not Started</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="submitted">Submitted</SelectItem>
                  </SelectContent>
                </Select>

                {/* Match Score */}
                {matchScores[c.id] ? (
                  <div className="mt-3 pt-3 border-t border-[#E2E2E2]" data-testid={`match-score-${c.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-3.5 w-3.5 text-[#D4AF37]" />
                        <span className="text-xs font-medium">Match Score</span>
                      </div>
                      <span className={`text-lg font-semibold ${
                        (matchScores[c.id].score || matchScores[c.id].match_score) >= 70 ? 'text-green-600' :
                        (matchScores[c.id].score || matchScores[c.id].match_score) >= 40 ? 'text-amber-600' : 'text-red-500'
                      }`} style={{ fontFamily: "'Playfair Display', serif" }}>
                        {matchScores[c.id].score || matchScores[c.id].match_score}/100
                      </span>
                    </div>
                    <Badge variant="outline" className={`text-xs mb-2 ${
                      (matchScores[c.id].score || matchScores[c.id].match_score) >= 70 ? 'bg-green-50 text-green-700 border-green-200' :
                      (matchScores[c.id].score || matchScores[c.id].match_score) >= 40 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-red-50 text-red-700 border-red-200'
                    }`}>{matchScores[c.id].category || matchScores[c.id].match_level || 'Target'}</Badge>
                    {matchScores[c.id].strengths && matchScores[c.id].strengths.slice(0, 2).map((s, i) => (
                      <p key={i} className="text-xs text-green-700 mt-0.5">+ {s}</p>
                    ))}
                    {matchScores[c.id].concerns && matchScores[c.id].concerns.slice(0, 2).map((s, i) => (
                      <p key={i} className="text-xs text-red-500 mt-0.5">- {s}</p>
                    ))}
                    {matchScores[c.id].recommendation && (
                      <p className="text-xs text-muted-foreground mt-2 italic">{matchScores[c.id].recommendation}</p>
                    )}
                    <Button size="sm" variant="ghost" className="text-xs h-7 mt-2 text-[#D4AF37]" onClick={() => handleMatchScore(c.id)} disabled={scoringId === c.id} data-testid={`rescore-${c.id}`}>
                      {scoringId === c.id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
                      Re-analyze
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full mt-3 text-xs h-8"
                    onClick={() => handleMatchScore(c.id)}
                    disabled={scoringId === c.id}
                    data-testid={`get-match-score-${c.id}`}
                  >
                    {scoringId === c.id ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1.5 text-[#D4AF37]" />}
                    Get Match Score
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
